<i18n locale="en">
  {
    "header": "What our guests are saying",
    "subtitle": "Check out our most recent reviews!"
  }
</i18n>
<template>
  <div class="reviews-preview">
    <PreviewSheet
      class="reviews-preview__callout"
      background="secondary"
      radius="large"
    >
      <div class="flex-col gap-14 justify-center align-center">
        <div
          class="reviews-preview__headline flex-col gap-4 justify-center align-center"
        >
          <PreviewTypography
            :text="t('header')"
            align="center"
            variant="heading2"
          />
          <PreviewTypography
            :text="t('subtitle')"
            align="center"
            color="secondary"
            variant="paragraphRegular"
          />
        </div>
        <div
          :class="`reviews-preview__reviews-grid reviews-preview__cols-${columns.length}`"
        >
          <div
            v-for="(reviewColumn, columnIndex) in columns"
            :key="`review-col-${columnIndex}`"
            class="flex-col gap-4"
          >
            <ReviewCard
              v-for="(review, index) in reviewColumn"
              :key="`review-${index}`"
              :review="review"
            />
          </div>
        </div>
      </div>
    </PreviewSheet>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PreviewSheet from '@/lib/builder/public/preview/PreviewSheet.vue'
import PreviewTypography from '@/lib/builder/public/preview/PreviewTypography.vue'

import type { ReviewsSection } from '../../../WebsiteBuilder.types'

import ReviewCard from './ReviewCard.vue'

type RenderedReview = ReviewsSection['params']['reviews'][number]

const props = defineProps<{
  content: ReviewsSection
}>()

const { t } = useI18n()

const columns = computed(() => {
  const reviews = props.content.params.reviews
  const numberOfColumns = reviews.length > 3 ? 4 : 3
  // Initialize columns array with empty arrays
  const columns: RenderedReview[][] = new Array(numberOfColumns)
    .fill(null)
    .map(() => [])

  // If no reviews, return the empty columns
  if (reviews.length === 0) {
    return columns
  }

  let index = 0
  reviews.forEach((review) => {
    columns[index]?.push(review)
    index += 1
    if (index >= columns.length) {
      index = 0
    }
  })

  return columns
})
</script>
<style lang="scss" scoped>
.reviews-preview {
  padding: 8px;

  &__callout {
    padding: 72px 112px;
  }

  &__headline {
    max-width: 672px;
    text-align: center;
  }

  &__reviews-grid {
    display: grid;
    gap: 16px;
  }

  &__cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &__cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
